package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{CCABServer, ExaminerTagCreateData, ExaminerTagUpdateData}
import com.neurobs.ccab.db.ExaminerTagData
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelDelegate}
import com.neurobs.js.util.StringUtilities.dateString
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.js.dom.RecordTableView.CommandLinkCell
import com.neurobs.js.dom.{InputTextBox, TextAreaTextBox}
import com.neurobs.js.view.{Form, Gui}
import scalatags.JsDom.all._

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.{Future, Promise}

class AdminExaminerTagListController(gui: Gui, server: CCABServer) extends AdminListAdapter[ExaminerTagData] {
	import AdminExaminerTagListController._

	override def title = "Examiner Tags"

	override def itemName = "Examiner Tag"

	override def columnHeaders = Seq(
		"Created",
		"Identifier",
		"Group Name",
		"Test Run Label",
		"Session Label",
		"Description",
		""
	)

	override def itemRow(t: ExaminerTagData, delegate: Option[AdminPanelDelegate], controller: AdminListController[ExaminerTagData]) = Seq(
		dateString(t.createdAt),
		t.identifier,
		t.groupName,
		t.displayNameTestRun.getOrElse(""): String,
		t.displayNameSession.getOrElse(""): String,
		t.description,
		CommandLinkCell("edit", () => createOrEdit(Some(t), controller))
	)

	override val create = Some(createOrEdit(None, _))

	override def get(): Future[Either[String, Seq[ExaminerTagData]]] = {
		val promise = Promise[Either[String, Seq[ExaminerTagData]]]
		server.getExaminerTags()._2 map {
			case Left(error) => promise.success(Left(error.description))
			case Right(data) => promise.success(Right(data))
		}
		promise.future
	}

	private def createOrEdit(t: Option[ExaminerTagData], c: AdminListController[ExaminerTagData]): Unit = {
		val boxWidth = 20
		val formWidth = 60

		val descriptionBox: TextAreaTextBox = textarea(style := s"width:${boxWidth}em;height:5em;").render
		descriptionBox.setValue(t.map(_.description).getOrElse(""))
		val identifierBox: InputTextBox = makeTextInput(boxWidth, t.map(_.identifier).getOrElse(""))
		val groupNameBox: InputTextBox = makeTextInput(boxWidth, t.map(_.groupName).getOrElse(""))
		val displayNameTestRunBox: InputTextBox = makeTextInput(boxWidth, t.map(_.displayNameTestRun.getOrElse("")).getOrElse(""))
		val displayNameSessionBox: InputTextBox = makeTextInput(boxWidth, t.map(_.displayNameSession.getOrElse("")).getOrElse(""))

		val form = makeForm(
			width = formWidth,
			title = t match {
				case Some(tag) => s"Update ${tag.identifier}"
				case None => "Create New Examiner Tag"
			},
			description = "",
			elements = Array(
				("Identifier:", t.map(t => span(t.identifier).render).getOrElse(identifierBox.text)),
				("Group Name:", groupNameBox.text),
				("Test Run Display Name:", displayNameTestRunBox.text),
				("Session Display Name:", displayNameSessionBox.text),
				("Description:", descriptionBox.area)
			),
			useCancel = true,
			submitCaption = if (t.isDefined) "Update" else "Create"
		)

		def process(callback: (Option[String]) => Unit): Either[String, Form.Process] = {
			if ((t.isEmpty && identifierBox.value().isEmpty) || (displayNameTestRunBox.value().isEmpty && displayNameSessionBox.value().isEmpty))
				Left("Display name and Identifier are both required.")
			else Right(
				t match {
					case Some(tag) =>
						val data = ExaminerTagUpdateData(
							groupName = groupNameBox.value(),
							displayNameTestRun = emptyStringOption(displayNameTestRunBox.value()),
							displayNameSession = emptyStringOption(displayNameSessionBox.value()),
							description = descriptionBox.value()
						)
						val call = server.updateExaminerTag(tag.id, data)
						call._2 map {
							case Left(err) => callback(Some(err.description))
							case Right(_) =>
								callback(None)
								c.getList()
						}
						call._1
					case None =>
						val data = ExaminerTagCreateData(
							identifier = identifierBox.value(),
							groupName = groupNameBox.value(),
							displayNameTestRun = emptyStringOption(displayNameTestRunBox.value()),
							displayNameSession = emptyStringOption(displayNameSessionBox.value()),
							description = descriptionBox.value()
						)
						val call = server.createExaminerTag(data)
						call._2 map {
							case Left(err) => callback(Some(err.description))
							case Right(_) =>
								callback(None)
								c.getList()
						}
						call._1
				}
			)
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}
}

object AdminExaminerTagListController {
	def apply(gui: Gui, server: CCABServer): AdminListController[ExaminerTagData] = new AdminListController[ExaminerTagData](new AdminExaminerTagListController(gui, server))

	def emptyStringOption(s: String): Option[String] = if (s.isEmpty) None else Some(s)
}